import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import KpiCard from '../components/KpiCard';
import Pagination, { PAGE_SIZE, usePagedRows } from '../components/Pagination';
import { useToast } from '../components/Toast';
import { fetchAllPages } from '../api/fetchAll';
import {
  RentalApplication,
  ApplicationStatus,
  APPLICATION_STATUSES,
  listApplications,
  updateApplicationStatus,
  statusLabel,
  statusBadgeClass,
  formatMoney,
  extractApiError,
} from '../api/applications';

type StatusFilter = 'all' | ApplicationStatus;
const STATUS_FILTERS: StatusFilter[] = ['all', ...APPLICATION_STATUSES];

const ApplicationOrders: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [apps, setApps] = useState<RentalApplication[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState<string>('');
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Every matching application: the KPI cards count them all.
      const all = await fetchAllPages((page, limit) =>
        listApplications({
          status: status === 'all' ? undefined : status,
          search: search.trim() || undefined,
          page,
          limit,
        }).then((res) => ({ rows: res.applications, totalPages: res.totalPages }))
      );
      setApps(all);
    } catch (err) {
      toast(extractApiError(err, 'Failed to load applications'), 'error');
    } finally {
      setLoading(false);
    }
  }, [status, search, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (app: RentalApplication, next: ApplicationStatus) => {
    if (next === app.status) return;
    setSavingId(app.id);
    try {
      const updated = await updateApplicationStatus(app.id, next);
      // Merge the whole server row (re-fetched with includes).
      setApps((prev) => prev.map((a) => (a.id === app.id ? { ...a, ...updated } : a)));
      toast(`${app.referenceNo || 'Application'} → ${statusLabel(next)}`, 'success');
      if (status !== 'all' && updated.status !== status) {
        setApps((prev) => prev.filter((a) => a.id !== app.id));
      }
    } catch (err) {
      toast(extractApiError(err, 'Failed to update status'), 'error');
    } finally {
      setSavingId(null);
    }
  };

  const kpis = useMemo(() => {
    const submitted = apps.filter((a) => a.status === 'submitted').length;
    const underReview = apps.filter((a) => a.status === 'under_review').length;
    const approved = apps.filter((a) => a.status === 'approved').length;
    return { total: apps.length, submitted, underReview, approved };
  }, [apps]);

  const { page, setPage, pageRows, total } = usePagedRows(apps, PAGE_SIZE, `${status}|${search}`);

  return (
    <div>
      <div className="flex gap-4 flex-wrap mb-6">
        <KpiCard icon="file-text" label="Total Applications" value={String(kpis.total)} change="This view" up delay={0} />
        <KpiCard icon="message-square" label="Submitted" value={String(kpis.submitted)} change="Awaiting review" up delay={80} />
        <KpiCard icon="eye" label="Under Review" value={String(kpis.underReview)} change="Being reviewed" up delay={160} />
        <KpiCard icon="check-circle" label="Approved" value={String(kpis.approved)} change="All time" up delay={240} />
      </div>

      {/* Filters + search */}
      <div className="flex justify-between items-end flex-wrap gap-3 mb-5">
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-xs font-semibold text-g500 uppercase tracking-wider w-14">Status</span>
          {STATUS_FILTERS.map((s) => {
            const active = status === s;
            return (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className="capitalize"
                style={{
                  padding: '7px 15px',
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  border: `1px solid ${active ? 'var(--brand)' : 'var(--g200)'}`,
                  background: active ? 'var(--brand-light)' : '#fff',
                  color: active ? 'var(--brand)' : 'var(--g600)',
                }}
              >
                {s === 'all' ? 'all' : statusLabel(s as ApplicationStatus)}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            placeholder="Search name or phone…"
            style={{
              padding: '9px 14px',
              borderRadius: 10,
              fontSize: 13,
              border: '1px solid var(--g200)',
              minWidth: 220,
            }}
          />
          <button className="btn btn-secondary btn-sm" onClick={() => load()}>
            <Icon name="search" size={14} /> Search
          </button>
        </div>
      </div>

      <div
        className="bg-white rounded-card overflow-x-auto border border-g100"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }}
      >
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {['Ref', 'Applicant', 'Property', 'Monthly Income', 'Fee', 'Status', 'Update Status', 'View'].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-xs font-semibold text-g500 uppercase tracking-wider border-b border-g200 bg-g50 whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-sm text-g500">
                  Loading applications…
                </td>
              </tr>
            ) : apps.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-sm text-g500">
                  No applications match this filter.
                </td>
              </tr>
            ) : (
              pageRows.map((a) => (
                <tr key={a.id}>
                  <td className="px-4 py-3.5 text-sm font-semibold whitespace-nowrap border-b border-g100">
                    {a.referenceNo || `#${a.id}`}
                  </td>
                  <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                    <div className="font-semibold">{a.fullName}</div>
                    <div className="text-[12px] text-g500">
                      {a.phone || a.applicant?.email || `User #${a.userId}`}
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                    <span className="flex items-center gap-2">
                      <Icon name="map-pin" size={14} color="var(--g400)" />
                      {a.listing?.title || a.listing?.address || `Listing #${a.listingId}`}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm font-semibold whitespace-nowrap border-b border-g100">
                    {formatMoney(a.monthlyIncome)}
                  </td>
                  <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                    {/* The fee is charged before an application is stored, so
                        every application here is paid. */}
                    <span className="inline-flex items-center gap-1" style={{ color: 'var(--green)' }}>
                      <Icon name="check-circle" size={14} color="var(--green)" /> {formatMoney(a.applicationFee)}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                    <span className={statusBadgeClass(a.status)}>{statusLabel(a.status)}</span>
                  </td>
                  <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                    <select
                      value={a.status}
                      disabled={savingId === a.id}
                      onChange={(e) => changeStatus(a, e.target.value as ApplicationStatus)}
                      style={{
                        padding: '7px 10px',
                        borderRadius: 9,
                        fontSize: 13,
                        fontWeight: 600,
                        border: '1px solid var(--g200)',
                        background: savingId === a.id ? 'var(--g100)' : '#fff',
                        color: 'var(--g700)',
                        cursor: savingId === a.id ? 'wait' : 'pointer',
                      }}
                    >
                      {APPLICATION_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {statusLabel(s)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                    <button
                      onClick={() => navigate(`/admin/applications/${a.id}`)}
                      title="View full application"
                      className="inline-flex items-center gap-1.5"
                      style={{
                        padding: '7px 14px',
                        borderRadius: 9,
                        fontSize: 13,
                        fontWeight: 600,
                        border: '1px solid var(--g200)',
                        background: '#fff',
                        color: 'var(--g700)',
                        cursor: 'pointer',
                      }}
                    >
                      <Icon name="eye" size={14} color="var(--brand)" />
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} disabled={loading} />
    </div>
  );
};

export default ApplicationOrders;
